import { HttpClient } from '@angular/common/http';
import { injectRoot, objectToHttpParams } from '@kp/utils';
import { catchError, map, tap, throwError } from 'rxjs';

import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

type DefaultName = ((header?: string | null) => string) | string;
type DownloadContext = {
  defaultName: DefaultName;
  targetWindow: Window | null | undefined;
};
type FileRequest = {
  body: RequestBody | undefined;
  method: RequestMethod;
  url: string;
};

type RequestBody = Array<unknown> | Record<string, unknown>;

type RequestMethod = 'get' | 'post';

type SaveFileOptions = {
  body?: RequestBody;
  defaultName?: DefaultName;
  http?: HttpClient;
  method?: RequestMethod;
  targetWindow?: Window | null | undefined;
  url: string;
};

const BLOB_URL_LIFETIME = 60_000;

const getFileName = (header?: string | null): string => {
  const contentDisposition = header?.split("''")[1];

  return contentDisposition ? decodeURIComponent(contentDisposition) : 'kp.pdf';
};

const isIOS = (): boolean =>
  typeof navigator !== 'undefined' &&
  // eslint-disable-next-line @stylistic/wrap-regex
  (/iPad|iPhone|iPod/u.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1));

/**
 * Na iOS musí být tato funkce zavolána synchronně přímo
 * v obsluze uživatelského kliknutí.
 *
 * undefined = nové okno není na dané platformě potřeba
 * null = otevření nového okna bylo zablokováno
 * Window = nové okno bylo úspěšně otevřeno
 */
const prepareDownloadWindow = (): Window | null | undefined => {
  if (!isIOS()) {
    return undefined;
  }

  const targetWindow = globalThis.open('about:blank', '_blank');

  if (targetWindow) {
    targetWindow.opener = null;
  }

  return targetWindow;
};

const revokeBlobUrlLater = (blobUrl: string): void => {
  globalThis.setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, BLOB_URL_LIFETIME);
};

const openBlobOnIOS = (
  blobUrl: string,
  targetWindow: Window | null | undefined,
): void => {
  if (!targetWindow || targetWindow.closed) {
    URL.revokeObjectURL(blobUrl);

    throw new Error('Unable to open a new window for the downloaded file');
  }

  targetWindow.location.replace(blobUrl);
  revokeBlobUrlLater(blobUrl);
};

// oxlint-disable-next-line max-statements
const downloadUsingLink = (blobUrl: string, name: string): void => {
  const link = document.createElement('a');

  link.href = blobUrl;
  link.target = '_blank';
  link.download = name;

  document.body.append(link);

  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: globalThis.window,
    }),
  );

  link.remove();
  revokeBlobUrlLater(blobUrl);
};

function downloadBlob(
  blob: string | Blob,
  name = 'file.txt',
  targetWindow?: Window | null,
): void {
  const normalizedBlob =
    typeof blob === 'string' ? new Blob([blob], { type: 'text/plain' }) : blob;

  const blobUrl = URL.createObjectURL(normalizedBlob);

  if (isIOS()) {
    openBlobOnIOS(blobUrl, targetWindow);

    return;
  }

  downloadUsingLink(blobUrl, name);
}

const resolveFileName = (
  response: HttpResponse<Blob>,
  defaultName: DefaultName,
): string => {
  const header = response.headers.get('content-disposition');

  return typeof defaultName === 'function' ? defaultName(header) : defaultName;
};

const getResponseBlob = (response: HttpResponse<Blob>): Blob => {
  if (!response.body) {
    throw new Error('Unable to save file');
  }

  const contentType =
    response.headers.get('content-type') ?? 'application/octet-stream';

  return new Blob([response.body], {
    type: contentType,
  });
};

const processResponse = (
  response: HttpResponse<Blob>,
  context: DownloadContext,
): void => {
  const fileName = resolveFileName(response, context.defaultName);

  const blob = getResponseBlob(response);

  downloadBlob(blob, fileName, context.targetWindow);
};

const closeTargetWindow = (targetWindow: Window | null | undefined): void => {
  if (targetWindow && !targetWindow.closed) {
    targetWindow.close();
  }
};

const createGetRequest = (
  http: HttpClient,
  request: FileRequest,
): Observable<HttpResponse<Blob>> => {
  const params = request.body ? objectToHttpParams(request.body) : {};

  return http.get(request.url, {
    observe: 'response',
    params,
    responseType: 'blob',
  });
};

const createPostRequest = (
  http: HttpClient,
  request: FileRequest,
): Observable<HttpResponse<Blob>> =>
  http.post(request.url, request.body ?? null, {
    observe: 'response',
    responseType: 'blob',
  });

const createRequest = (
  http: HttpClient,
  request: FileRequest,
): Observable<HttpResponse<Blob>> =>
  request.method === 'post'
    ? createPostRequest(http, request)
    : createGetRequest(http, request);

function saveFile$({
  body,
  defaultName = getFileName,
  http = injectRoot(HttpClient),
  method = 'get',
  targetWindow,
  url,
}: SaveFileOptions): Observable<void> {
  const request: FileRequest = {
    body,
    method,
    url,
  };

  const context: DownloadContext = {
    defaultName,
    targetWindow,
  };

  return createRequest(http, request).pipe(
    tap(response => {
      processResponse(response, context);
    }),
    map(() => undefined),
    catchError((error: unknown) => {
      closeTargetWindow(targetWindow);

      return throwError(() => error);
    }),
  );
}

export { downloadBlob, prepareDownloadWindow, saveFile$ };
